import { useState } from 'react';
import { Button } from '@evertrue/et-components';
import PropTypes from 'prop-types';

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})*$/;
const DEFAULT_EMAIL_LOCAL = 'outreach';

/**
 * Inline form for creating a Postmark domain. The default sending email is
 * hardcoded to outreach@<domain> — users no longer pick it.
 *
 * The submit callback still receives (domain, fullEmail) so the create path on
 * the hook stays unchanged; we just compute the email automatically.
 */
const DnsSetupForm = ({
  onSubmit,
  onCancel,
  submitLabel = 'Generate DNS Records',
  placeholder = 'e.g. mail.yourorg.com',
  isUpdate = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');

  const validateDomain = (value) => {
    if (!value.trim()) return 'Domain name is required.';
    if (!DOMAIN_REGEX.test(value.trim())) return 'Please enter a valid domain (e.g. mail.yourorg.com).';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const domainErr = validateDomain(inputValue);
    if (domainErr) {
      setValidationError(domainErr);
      return;
    }
    setValidationError('');
    const domain = inputValue.trim();
    const fullEmail = `${DEFAULT_EMAIL_LOCAL}@${domain}`;
    onSubmit(domain, fullEmail);
  };

  return (
    <form className="dns-setup-form" onSubmit={handleSubmit} noValidate>
      {!isUpdate && (
        <div className="dns-setup-form--info-banner">
          <strong>Tip:</strong> We recommend using a subdomain like <code>mail.yourorg.com</code> so your primary domain
          is not affected by email sending reputation.
        </div>
      )}

      <div className="dns-setup-form--field">
        <label htmlFor="dns-domain-input" className="dns-setup-form--label">
          Sending Domain
        </label>
        <input
          id="dns-domain-input"
          type="text"
          className="dns-setup-form--input"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (validationError) setValidationError('');
          }}
        />
        {validationError && <span className="dns-setup-form--error">{validationError}</span>}
      </div>

      <div className="dns-setup-form--actions">
        <Button type="default" onClick={handleSubmit}>
          {submitLabel}
        </Button>
        <button type="button" className="dns-setup-form--cancel-link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

DnsSetupForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  placeholder: PropTypes.string,
  isUpdate: PropTypes.bool,
};

export default DnsSetupForm;
